// src/config/sportsbookCatalog.js
//
// Shared sportsbook / DFS / trading-market catalog used by the Homepage's
// state-specific "available platforms" section.
//
// This mirrors the SPORTSBOOKS + STATE_BOOKS data already used by
// PromotionsPage.js and PromoFinderPage.js so platform availability per state
// stays a single, consistent source of truth rather than inventing new data.

export const PLATFORM_CATEGORIES = {
  TRADING: 'trading',
  DFS: 'dfs',
  SPORTSBOOK: 'sportsbook',
};

// key -> catalog entry (label + icon mirror HP_BOOKMAKER_ICONS / PromotionsPage.js SPORTSBOOKS)
export const PLATFORM_CATALOG = {
  draftkings:      { label: 'DraftKings',          icon: '/images/draftkings_icon.png',  category: PLATFORM_CATEGORIES.SPORTSBOOK },
  fanduel:         { label: 'FanDuel',              icon: '/images/fanduel_icon.png',      category: PLATFORM_CATEGORIES.SPORTSBOOK },
  betmgm:          { label: 'BetMGM',               icon: '/images/bet_mgm_icon.png',      category: PLATFORM_CATEGORIES.SPORTSBOOK },
  williamhill_us:  { label: 'Caesars',              icon: '/images/caesars_icon.png',      category: PLATFORM_CATEGORIES.SPORTSBOOK },
  hardrockbet:     { label: 'Hard Rock Bet',        icon: '/images/hard_rock_bet_icon.png', category: PLATFORM_CATEGORIES.SPORTSBOOK },
  //hardrockbet_az:  { label: 'Hard Rock Bet (AZ)',   icon: '/images/hard_rock_bet_icon.png', category: PLATFORM_CATEGORIES.SPORTSBOOK },
  //hardrockbet_fl:  { label: 'Hard Rock Bet (FL)',   icon: '/images/hard_rock_bet_icon.png', category: PLATFORM_CATEGORIES.SPORTSBOOK },
  hardrockbet_oh:  { label: 'Hard Rock Bet (OH)',   icon: '/images/hard_rock_bet_icon.png', category: PLATFORM_CATEGORIES.SPORTSBOOK },
  espnbet:         { label: 'theScore Bet',         icon: '/images/espn_bet_icon.png',      category: PLATFORM_CATEGORIES.SPORTSBOOK },
  betrivers:       { label: 'BetRivers',            icon: '/images/betrivers_icon.png',     category: PLATFORM_CATEGORIES.SPORTSBOOK },
  fanatics:        { label: 'Fanatics',             icon: '/images/fanatics_icon.png',      category: PLATFORM_CATEGORIES.SPORTSBOOK },
  ballybet:        { label: 'Bally Bet',            icon: '/images/bally_bet_icon.png',     category: PLATFORM_CATEGORIES.SPORTSBOOK },
  pick6:           { label: 'DraftKings Pick6',     icon: '/images/pick6.png',              category: PLATFORM_CATEGORIES.DFS },
  prizepicks:      { label: 'PrizePicks',           icon: '/images/prizepicks.jpg',         category: PLATFORM_CATEGORIES.DFS },
  underdog:        { label: 'Underdog',             icon: '/images/underdog.png',           category: PLATFORM_CATEGORIES.DFS },
  betr_us_dfs:     { label: 'Betr DFS',              icon: '/images/betr.jpg',              category: PLATFORM_CATEGORIES.DFS },
  kalshi:          { label: 'Kalshi',               icon: '/images/kalshi.png',             category: PLATFORM_CATEGORIES.TRADING },
  novig:           { label: 'Novig',                icon: '/images/novig.png',              category: PLATFORM_CATEGORIES.TRADING },
  polymarket:      { label: 'Polymarket',           icon: '/images/polymarket.png',         category: PLATFORM_CATEGORIES.TRADING },
  prophetx:        { label: 'ProphetX',             icon: '/images/prophetx.png',           category: PLATFORM_CATEGORIES.TRADING },
};

// Title string (as used in STATE_BOOKS below) -> catalog key.
const TITLE_TO_KEY = {
  'DraftKings': 'draftkings',
  'FanDuel': 'fanduel',
  'BetMGM': 'betmgm',
  'Caesars': 'williamhill_us',
  'Hard Rock Bet': 'hardrockbet',
  'Hard Rock Bet (AZ)': 'hardrockbet_az',
  'Hard Rock Bet (FL)': 'hardrockbet_fl',
  'Hard Rock Bet (OH)': 'hardrockbet_oh',
  'ESPN BET': 'espnbet',
  'theScore Bet': 'espnbet',
  'BetRivers': 'betrivers',
  'Fanatics': 'fanatics',
  'Bally Bet': 'ballybet',
  'DraftKings Pick6': 'pick6',
  'Pick6': 'pick6',
  'PrizePicks': 'prizepicks',
  'PrizePicks Fantasy': 'prizepicks',
  'Underdog Fantasy': 'underdog',
  'Underdog': 'underdog',
  'Betr Picks': 'betr_us_dfs',
  'Betr DFS': 'betr_us_dfs',
  'Kalshi': 'kalshi',
  'Novig': 'novig',
  'Polymarket': 'polymarket',
  'ProphetX': 'prophetx',
};

// Per-state platform availability — mirrors STATE_BOOKS in PromotionsPage.js
// and PromoFinderPage.js (kept in sync manually; this is the same business
// data, not a new/invented data set).
// Note: Polymarket and ProphetX are CFTC-regulated prediction/event-contract
// markets like Kalshi and Novig, so they follow the same state footprint —
// added everywhere Kalshi/Novig appear, and excluded where those are excluded
// (NJ, NV) due to state gaming-law conflicts/cease-and-desist actions.
const STATE_BOOKS = {
  OH: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'Hard Rock Bet (OH)',
    'Bally Bet', 'BetRivers', 'ESPN BET', 'Fanatics',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  IL: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  MD: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  PA: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  VA: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'Bally Bet', 'Hard Rock Bet',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  WV: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  IN: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'Bally Bet', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  KS: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  MA: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  TN: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'BetRivers', 'Hard Rock Bet',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  CO: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'BetRivers', 'Bally Bet',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  MI: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'Hard Rock Bet',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NC: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'Hard Rock Bet',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  KY: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NY: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  VT: ['DraftKings', 'FanDuel',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  WY: ['DraftKings', 'FanDuel',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  FL: ['Hard Rock Bet', 'Hard Rock Bet (FL)',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX', 'DraftKings Pick6'],
  MS: ['BetMGM',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NH: ['DraftKings',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  OR: ['DraftKings',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  DC: ['FanDuel', 'Caesars', 'ESPN BET',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  AZ: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'Fanatics',
    'BetRivers', 'Bally Bet', 'Hard Rock Bet (AZ)', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NJ: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'Bally Bet', 'Hard Rock Bet', 'BetRivers',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks'],
  IA: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'BetRivers', 'Hard Rock Bet', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  LA: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics', 'Hard Rock Bet',
    'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  CT: ['DraftKings', 'FanDuel', 'Fanatics', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  ME: ['DraftKings', 'FanDuel', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  DE: ['BetRivers', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NV: ['BetMGM', 'Caesars'],
  AR: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  MT: ['Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NE: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  NM: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  ND: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  RI: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  SD: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  WA: ['Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  WI: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  // No legal online sportsbooks yet — DFS + trading markets operate nationally.
  AL: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  AK: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  CA: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  GA: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  MN: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  OK: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  SC: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  TX: ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  // Sports betting legalized via 2024 ballot measure, live by 2025.
  MO: ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN BET', 'Fanatics',
    'PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  // Near-total gambling bans exclude DFS contest operators; trading markets still operate.
  HI: ['Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  ID: ['Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
  // Constitutional ban on all gambling, including DFS; trading markets still operate.
  UT: ['Kalshi', 'Novig', 'Polymarket', 'ProphetX'],
};

// Fallback only for unmapped codes (e.g. US territories); all 50 states + DC
// are now explicit above — matches the generic messaging already used in
// stateContent.js: "PrizePicks, Underdog Fantasy, Kalshi, Novig, Polymarket,
// and ProphetX are available in most US states."
const NATIONAL_FALLBACK_BOOKS = ['PrizePicks', 'Underdog Fantasy', 'Betr Picks', 'Kalshi', 'Novig', 'Polymarket', 'ProphetX'];

/**
 * Returns the platforms available in a given state, grouped into the three
 * supported categories (trading markets, DFS apps, sportsbooks).
 */
export function getStatePlatforms(stateCode) {
  const titles = (stateCode && STATE_BOOKS[stateCode]) || NATIONAL_FALLBACK_BOOKS;
  const seen = new Set();
  const trading = [];
  const dfs = [];
  const sportsbooks = [];

  for (const title of titles) {
    const key = TITLE_TO_KEY[title];
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const entry = PLATFORM_CATALOG[key];
    if (!entry) continue;

    const item = { key, label: entry.label, icon: entry.icon };
    if (entry.category === PLATFORM_CATEGORIES.TRADING) trading.push(item);
    else if (entry.category === PLATFORM_CATEGORIES.DFS) dfs.push(item);
    else sportsbooks.push(item);
  }

  return {
    trading,
    dfs,
    sportsbooks,
    total: trading.length + dfs.length + sportsbooks.length,
  };
}
